import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Checkbox } from '@factorialco/deprecated-design-system'
import {
  AtsJobPostingStatusesEnum,
  AtsPublishJobPostingDocument,
  AtsPublishJobPostingFragment,
} from 'generated/resources/graphql'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import { joinAsSentence } from 'modules/core/lib/stringUtils'
import app from 'modules/core/store/models/app'

import { isJobOpen } from 'modules/ats/lib/utils/jobPosting'
import { useJobPostingPreview } from 'modules/ats/lib/utils/useJobPostingPreview'

import { PublishModalOptions } from './PublishModalOptions'

interface Form {
  allowInternalApply: boolean
  allowReferral: boolean
  status: AtsJobPostingStatusesEnum
}

interface Props {
  job: AtsPublishJobPostingFragment
  onClose: () => void
  onSuccess: (value: AtsJobPostingStatusesEnum) => void
}

export const PublishJobPostingModal = ({ job, onClose, onSuccess }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const getPreviewUrl = useJobPostingPreview({ id: job.id, status: job.status })

  const wasInDraft = job.status === 'draft'

  const hasDescription = job.description && job.description.length > 0

  const showEditButton = !hasDescription

  const [publish] = useMutation(AtsPublishJobPostingDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const form = useForm<Form>({
    defaultValues: {
      allowInternalApply: job.allowInternalApply ?? false,
      allowReferral: job.allowReferral ?? false,
      status: job.status === 'published' ? 'unlisted' : 'published',
    },
    async onSubmit(values, { setErrors }) {
      const result = await publish({
        variables: {
          id: job.id,
          allowInternalApply: values.allowInternalApply,
          allowReferral: values.allowReferral,
          status: values.status,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (wasInDraft) {
        track(trackingEvents.ats.publishJobOpeningSubmit, {
          is_successful: handled.success,
          error_code: !handled.success && result.toString(),
          target_job_status: values.status,
        })
      }

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        dispatch({
          type: EventTypes.JOB_POSTING_PUBLISHED,
        })

        onSuccess(values.status)
      }

      return handled
    },
  })

  const missingFieldsErrors = () => {
    const missingFields: string[] = []

    if (!hasDescription) {
      missingFields.push(i18n.t('ats.job_details.publish_confirmation.missing_fields.description'))
    }

    if (!job.location?.id) {
      missingFields.push(i18n.t('ats.job_details.publish_confirmation.missing_fields.location'))
    }

    return missingFields
  }

  const missingFields = missingFieldsErrors()

  const title =
    job.status === 'published'
      ? i18n.t('ats.job_details.publish_confirmation.publish_unlisted.title')
      : i18n.t('ats.job_details.publish_confirmation.title')

  const description =
    job.status === 'published'
      ? i18n.t('ats.job_details.publish_confirmation.publish_unlisted.description')
      : job.status === 'unlisted'
      ? i18n.t('ats.job_details.publish_confirmation.description')
      : ''

  const onCloseModal = async () => {
    await track(trackingEvents.ats.publishJobOpeningCancel, {
      location_id: job.location?.id,
      description: job.description,
    })

    onClose()
  }

  const onClickPreview = async () => {
    const previewUrl = await getPreviewUrl()

    window.open(previewUrl, '_blank')
  }

  const onClickEdit = () => {
    onCloseModal()

    navigate(
      `${navigation.ats.jobs.jobsId.settings.fullPath({
        jobId: job.id,
      })}?section=description`
    )
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      footer={
        <>
          <Button onClick={() => onClickPreview()} type="secondary">
            {i18n.t('ats.job_details.preview_job')}
          </Button>
          {showEditButton ? (
            <Button onClick={() => onClickEdit()}>
              {i18n.t('ats.job_posting_settings.edit_job_button')}
            </Button>
          ) : (
            <Button disabled={!hasDescription} submit>
              {i18n.t('ats.job_details.publish_confirmation.confirm')}
            </Button>
          )}
        </>
      }
      onClose={onCloseModal}
      onSubmit={() => form.handleSubmit()}
    >
      <Box gap="s16">
        {missingFields.length > 0 && (
          <Alert color="warning">
            {i18n.t('ats.job_details.publish_confirmation.missing_fields.message', {
              missing_fields: joinAsSentence(missingFields),
            })}
          </Alert>
        )}
        {hasDescription && !isJobOpen(job.status) && (
          <PublishModalOptions
            onChange={(value) => form.setFieldValue('status', value)}
            currentStatus={form.values.status}
          />
        )}

        <Box gap="s16">
          <Checkbox
            checked={form.values.allowReferral}
            label={i18n.t(
              'ats.job_details.publish_confirmation.open_positions.allow_referral_label'
            )}
            onChange={(event) => form.setFieldValue('allowReferral', event.target.checked)}
          />
          <Box flexDirection="row" gap="s12" alignItems="center">
            <Checkbox
              checked={form.values.allowInternalApply}
              label={i18n.t(
                'ats.job_details.publish_confirmation.open_positions.allow_internal_apply_label'
              )}
              onChange={(event) => form.setFieldValue('allowInternalApply', event.target.checked)}
            />
            <InformationTooltip
              information={i18n.t(
                'ats.job_details.publish_confirmation.open_positions.allow_internal_apply_tooltip'
              )}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
